import type { RouteParamsGeneric, RouteRecordRaw } from 'vue-router'

import type { SectionId } from './sections'

import type { ScreenId } from '@/generated/screens.g'
import SectionLayout from '@/components/shell/SectionLayout/SectionLayout.vue'
import { BUILT_ONBOARDING_CHAIN, ONBOARDING_CHAIN_STEPS } from '@/types/onboarding-chain'

declare module 'vue-router' {
  interface RouteMeta {
    title?: string
    requiresAuth?: boolean
    requiresWorkspace?: boolean
    requiresEntitlement?: boolean
    guestOnly?: boolean
    /** How far into a section the page sits: 0 for a section root or tab, 1 for a detail, 2 for one deeper. Drives the navigation motion. */
    depth?: number
    /** Routes sharing a key keep one mounted view when navigating between them; a function takes the route's params and returns the key. */
    viewKey?: string | ((params: RouteParamsGeneric) => string)
    /** i18n key of the one-sentence lead a section layout shows for this tab. */
    description?: string
    /** The catalogue screen this route shows, or `null` for a route that is no screen of the app. */
    screen: ScreenId | null
  }
}

const SHELL_META = {
  layout: 'shell' as const,
  requiresAuth: true,
  requiresWorkspace: true,
  requiresEntitlement: true,
  depth: 0,
}

const DETAIL_META = { ...SHELL_META, depth: 1 }

function section(
  id: SectionId,
  path: string,
  children: RouteRecordRaw[],
  redirect?: string,
): RouteRecordRaw {
  return {
    path,
    component: SectionLayout,
    props: { section: id },
    meta: { ...SHELL_META, screen: null },
    redirect,
    children,
  }
}

export const routes: RouteRecordRaw[] = [
  {
    // Neither chat route renders a view; the shell mounts the chat surface (see App.vue).
    path: '/',
    name: 'home',
    component: { render: () => null },
    meta: { ...SHELL_META, screen: 'home' },
  },
  {
    path: '/chat/:id',
    name: 'chat',
    component: { render: () => null },
    meta: { ...SHELL_META, title: 'routes.chat', screen: 'chat' },
  },
  {
    path: '/chats',
    name: 'chats',
    component: () => import('../views/ChatsView.vue'),
    meta: { ...SHELL_META, title: 'routes.chats', screen: 'chats' },
  },
  section('inbox', '/inbox', [
    {
      path: '',
      name: 'inbox',
      component: () => import('../views/InboxView.vue'),
      meta: { title: 'nav.inbox', description: 'nav.description.inbox', screen: 'inbox' },
    },
    {
      path: 'sent',
      name: 'inbox-sent',
      component: () => import('../views/InboxView.vue'),
      meta: { title: 'tabs.sent', description: 'nav.description.inbox', screen: 'inbox.sent' },
    },
    {
      path: 'archive',
      name: 'inbox-archive',
      component: () => import('../views/InboxView.vue'),
      meta: {
        title: 'tabs.archive',
        description: 'nav.description.inbox',
        screen: 'inbox.archive',
      },
    },
  ]),
  {
    path: '/calendar',
    name: 'calendar',
    component: () => import('../views/ContentView.vue'),
    meta: { ...SHELL_META, title: 'nav.calendar', viewKey: 'calendar', screen: 'calendar' },
  },
  {
    path: '/calendar/canvas',
    name: 'calendar-canvas',
    component: () => import('../views/ContentView.vue'),
    meta: {
      ...SHELL_META,
      title: 'content.canvasLabel',
      viewKey: 'calendar',
      screen: 'calendar.canvas',
    },
  },
  {
    path: '/calendar/schedule',
    name: 'calendar-schedule',
    component: () => import('../views/CalendarScheduleView.vue'),
    meta: { ...DETAIL_META, title: 'routes.publishingSchedule', screen: 'calendar.schedule' },
  },
  {
    path: '/pages',
    name: 'pages',
    component: () => import('../views/PagesView.vue'),
    meta: { ...SHELL_META, title: 'nav.pages', screen: 'pages' },
  },
  {
    path: '/pages/topics',
    redirect: { name: 'pages' },
  },
  {
    path: '/pages/topics/:id',
    name: 'topic',
    component: () => import('../views/StrategyTopicView.vue'),
    meta: { ...DETAIL_META, title: 'nav.kind.topic', screen: 'strategy.topic' },
    props: true,
  },
  {
    path: '/pages/topics/:id/research',
    name: 'topic-research',
    component: () => import('../views/StrategyTopicResearchView.vue'),
    meta: { ...DETAIL_META, title: 'nav.research', screen: 'strategy.topic.research' },
    props: true,
  },
  {
    path: '/pages/:id',
    name: 'page',
    component: () => import('../views/PageWorkbenchView.vue'),
    meta: {
      ...DETAIL_META,
      title: 'nav.kind.page',
      viewKey: (params) => `page:${String(params.id)}`,
      screen: 'page',
    },
    props: true,
  },
  {
    path: '/pages/:id/:tab(research|draft|visibility)',
    redirect: (to) => ({ name: 'page', params: { id: to.params.id }, query: to.query }),
  },
  {
    path: '/strategy/pages/:id',
    redirect: (to) => ({ name: 'page', params: { id: to.params.id } }),
  },
  {
    path: '/posts',
    name: 'posts',
    component: () => import('../views/PostsView.vue'),
    meta: { ...SHELL_META, title: 'nav.posts', screen: 'posts' },
  },
  {
    path: '/content',
    redirect: { name: 'posts' },
  },
  {
    path: '/posts/:id',
    name: 'post',
    component: () => import('../views/PostWorkbenchView.vue'),
    meta: {
      ...DETAIL_META,
      title: 'nav.kind.post',
      viewKey: (params) => `post:${String(params.id)}`,
      screen: 'post',
    },
    props: true,
  },
  {
    path: '/posts/:id/:tab(research|draft|visibility)',
    redirect: (to) => ({ name: 'post', params: { id: to.params.id }, query: {} }),
  },
  {
    path: '/post/:id',
    redirect: (to) => ({ name: 'post', params: { id: to.params.id } }),
  },
  {
    path: '/article/:id',
    component: () => import('../views/ContentItemView.vue'),
    meta: { ...DETAIL_META, title: 'nav.kind.page', screen: null },
  },
  {
    // A bare piece link with no known shape - resolved once `content.posts` names the id.
    path: '/piece/:id',
    name: 'content-item',
    component: () => import('../views/ContentItemView.vue'),
    meta: { ...DETAIL_META, title: 'nav.calendar', screen: null },
    props: true,
  },
  section('strategy', '/strategy', [
    {
      path: '',
      name: 'strategy',
      component: () => import('../views/StrategyView.vue'),
      meta: {
        title: 'nav.arguments',
        description: 'nav.description.arguments',
        screen: 'strategy',
      },
    },
  ]),
  {
    path: '/strategy/topics',
    redirect: { name: 'pages' },
  },
  {
    path: '/strategy/topics/:id',
    redirect: (to) => ({ name: 'topic', params: { id: to.params.id } }),
  },
  {
    path: '/strategy/topics/:id/research',
    redirect: (to) => ({ name: 'topic-research', params: { id: to.params.id } }),
  },
  {
    path: '/strategy/research/:id',
    name: 'strategy-research-entry',
    component: () => import('../views/StrategyResearchEntryView.vue'),
    meta: { ...DETAIL_META, title: 'nav.kind.researchEntry', screen: 'strategy.research-entry' },
    props: true,
  },
  {
    path: '/strategy/arguments/:slug',
    name: 'strategy-argument',
    component: () => import('../views/StrategyArgumentView.vue'),
    meta: { ...DETAIL_META, title: 'nav.kind.argument', screen: 'strategy.argument' },
    props: true,
  },
  {
    path: '/strategy/schedule',
    redirect: { name: 'calendar-schedule' },
  },
  {
    path: '/strategy/plan',
    redirect: { name: 'pages' },
  },
  section(
    'media',
    '/media',
    [
      {
        path: 'uploads',
        name: 'media-uploads',
        component: () => import('../views/MediaView.vue'),
        meta: {
          title: 'media.uploads',
          description: 'nav.description.media',
          screen: 'media.uploads',
        },
      },
      {
        path: 'designs',
        name: 'media-designs',
        component: () => import('../views/MediaView.vue'),
        meta: {
          title: 'media.designs',
          description: 'nav.description.media',
          screen: 'media.designs',
        },
      },
      {
        path: 'templates',
        name: 'media-templates',
        component: () => import('../views/MediaView.vue'),
        meta: {
          title: 'media.templates',
          description: 'nav.description.media',
          screen: 'media.templates',
        },
      },
    ],
    '/media/uploads',
  ),
  {
    path: '/designs/:designId',
    name: 'design',
    component: () => import('../views/DesignView.vue'),
    meta: { ...DETAIL_META, title: 'routes.design', screen: 'design' },
    props: true,
  },
  section('visibility', '/visibility', [
    {
      path: '',
      name: 'visibility',
      component: () => import('../views/VisibilitySearchView.vue'),
      meta: {
        title: 'tabs.rankings',
        description: 'nav.description.visibility',
        screen: 'visibility',
      },
    },
    {
      path: 'ai-answers',
      name: 'visibility-ai-answers',
      component: () => import('../views/VisibilityAiAnswersView.vue'),
      meta: {
        title: 'tabs.aiAnswers',
        description: 'nav.description.visibility',
        screen: 'visibility.ai-answers',
      },
    },
    {
      path: 'social-media',
      name: 'visibility-social-media',
      component: () => import('../views/VisibilitySocialView.vue'),
      meta: {
        title: 'tabs.socialMedia',
        description: 'nav.description.visibility',
        screen: 'visibility.social',
      },
    },
  ]),
  {
    path: '/visibility/keywords/:id',
    component: () => import('../views/KeywordLinkView.vue'),
    meta: { ...DETAIL_META, title: 'nav.kind.keyword', screen: null },
  },
  {
    path: '/visibility/ai-answers/:promptId',
    name: 'visibility-prompt',
    component: () => import('../views/VisibilityPromptView.vue'),
    meta: { ...DETAIL_META, title: 'nav.kind.prompt', screen: 'visibility.prompt' },
    props: true,
  },
  {
    path: '/competitors',
    name: 'competitors',
    component: () => import('../views/CompetitorsView.vue'),
    meta: { ...SHELL_META, title: 'nav.competitors', screen: 'competitors' },
  },
  {
    path: '/competitors/:id',
    name: 'competitor',
    component: () => import('../views/CompetitorView.vue'),
    meta: { ...DETAIL_META, title: 'routes.competitor', screen: 'competitor' },
    props: true,
  },
  section(
    'identity',
    '/identity',
    [
      {
        path: 'brand-facts',
        name: 'identity-brand-facts',
        component: () => import('../views/IdentityBrandFactsView.vue'),
        meta: {
          title: 'tabs.brandFacts',
          description: 'nav.description.company',
          screen: 'identity.brand-facts',
        },
      },
      {
        path: 'customer-profile',
        name: 'identity-customer-profile',
        component: () => import('../views/IdentityCustomerProfileView.vue'),
        meta: {
          title: 'tabs.customerProfile',
          description: 'nav.description.customer',
          screen: 'identity.customer-profile',
        },
      },
    ],
    '/identity/brand-facts',
  ),
  section(
    'style',
    '/style',
    [
      {
        path: 'voice',
        name: 'style-voice',
        component: () => import('../views/BrandStyleGuideView.vue'),
        meta: {
          title: 'brand.artifacts.voice',
          description: 'nav.description.voice',
          screen: 'style.voice',
        },
      },
      {
        path: 'look',
        name: 'style-look',
        component: () => import('../views/BrandVisualStyleView.vue'),
        meta: {
          title: 'brand.artifacts.look',
          description: 'nav.description.look',
          screen: 'style.look',
        },
      },
    ],
    '/style/voice',
  ),
  {
    path: '/documents',
    name: 'documents',
    component: () => import('../views/DocumentsView.vue'),
    meta: { ...SHELL_META, title: 'routes.knowledge', screen: 'documents' },
  },
  {
    path: '/documents/:slug',
    name: 'document-page',
    component: () => import('../views/DocumentPageView.vue'),
    meta: { ...DETAIL_META, title: 'routes.document', screen: 'document' },
    props: true,
  },
  section(
    'settings',
    '/settings',
    [
      {
        path: 'account',
        name: 'settings-account',
        component: () => import('../views/SettingsAccountView.vue'),
        meta: {
          title: 'tabs.account',
          description: 'nav.description.settings',
          screen: 'settings.account',
        },
      },
      {
        path: 'billing',
        name: 'settings-billing',
        component: () => import('../views/SettingsBillingView.vue'),
        meta: {
          title: 'tabs.billing',
          description: 'nav.description.settings',
          screen: 'settings.billing',
        },
      },
      { path: 'plan', redirect: { name: 'settings-billing' } },
      {
        path: 'connections',
        name: 'settings-connections',
        component: () => import('../views/SettingsConnectionsView.vue'),
        meta: {
          title: 'brand.connections.title',
          description: 'nav.description.settings',
          screen: 'settings.connections',
        },
      },
    ],
    '/settings/account',
  ),
  {
    path: '/setup',
    redirect: `/setup/${BUILT_ONBOARDING_CHAIN[0] ?? ONBOARDING_CHAIN_STEPS[0]}`,
  },
  {
    path: '/setup/:step',
    name: 'setup',
    component: () => import('../views/SetupView.vue'),
    meta: { ...SHELL_META, title: 'routes.setup', screen: 'setup' },
    props: true,
  },
  {
    path: '/auth',
    name: 'auth-sign-in',
    component: () => import('../views/AuthSignInView.vue'),
    meta: { layout: 'bare', title: 'routes.signIn', guestOnly: true, screen: null },
  },
  {
    path: '/auth/email-sent',
    name: 'auth-email-sent',
    component: () => import('../views/AuthEmailSentView.vue'),
    meta: { layout: 'bare', title: 'routes.checkYourEmail', guestOnly: true, screen: null },
  },
  {
    path: '/auth/callback',
    name: 'auth-callback',
    component: () => import('../views/AuthCallbackView.vue'),
    meta: { layout: 'bare', title: 'routes.signingIn', screen: null },
  },
  {
    path: '/auth/full-name',
    name: 'auth-full-name',
    component: () => import('../views/AuthFullNameView.vue'),
    meta: { layout: 'bare', title: 'routes.yourName', requiresAuth: true, screen: null },
  },
  {
    path: '/auth/workspace-setup',
    name: 'auth-workspace-setup',
    component: () => import('../views/AuthWorkspaceSetupView.vue'),
    meta: { layout: 'bare', title: 'routes.workspaceSetup', requiresAuth: true, screen: null },
  },
  {
    path: '/billing',
    name: 'billing',
    component: () => import('../views/BillingView.vue'),
    meta: {
      screen: null,
      layout: 'bare',
      title: 'billing.title',
      requiresAuth: true,
      requiresWorkspace: true,
    },
  },
  {
    path: '/billing/return',
    name: 'billing-return',
    component: () => import('../views/BillingReturnView.vue'),
    meta: {
      screen: null,
      layout: 'bare',
      title: 'billing.title',
      requiresAuth: true,
      requiresWorkspace: true,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('../views/NotFoundView.vue'),
    meta: { layout: 'bare', title: 'errors.notFoundTitle', screen: null },
  },
]
